import { Select, TextInput } from '@neo4j-ndl/react';
import type { Address } from '@nx/state';
import cn from 'classnames';
import { useMemo } from 'react';

import type { Country } from './country-list';
import countryList from './country-list';

type CountryDropdownProps = React.ComponentProps<typeof Select<Country, false>>;

export type NameAndAddress = Address & {
  name?: string;
};

type StripeAddressFieldProps = {
  data: NameAndAddress;
  onChange: (data: Partial<NameAndAddress>) => void;
  withNameField?: boolean;
  disabled?: boolean;
  testIdPrefix?: string;
  className?: string;
};

const getPostalCodeText = (country?: string) => {
  switch (country) {
    case 'US':
      return 'Zip code';
    case 'GB':
      return 'Postcode';
    default:
      return 'Postal code';
  }
};

export default function StripeAddressField({
  testIdPrefix = '',
  data,
  onChange,
  withNameField,
  disabled,
  className,
}: StripeAddressFieldProps) {
  const handleInputChange = ({ target: { name: fieldName, value } }: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [fieldName]: value });

  const handleCountryChange: CountryDropdownProps['selectProps']['onChange'] = (newValue) =>
    newValue && onChange({ country: newValue.key });

  const selectedCountry = useMemo(
    () => countryList.find((c) => [c.label, c.value].includes(data.country)),
    [data.country],
  );

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-wrap gap-4">
        {withNameField === true && (
          <TextInput
            value={data.name}
            onChange={handleInputChange}
            isDisabled={disabled}
            isFluid
            isRequired
            className="flex-1"
            label="Full name"
            htmlAttributes={{
              name: 'name',
            }}
          />
        )}
        <TextInput
          value={data.line1 || ''}
          onChange={handleInputChange}
          isDisabled={disabled}
          isFluid
          label="Street address"
          className="flex-1"
          htmlAttributes={{
            name: 'line1',
            required: true,
            autoComplete: 'street-address',
            'data-testid': `${testIdPrefix}input-address1`,
          }}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <TextInput
          value={data.city || ''}
          onChange={handleInputChange}
          isDisabled={disabled}
          isFluid
          className="flex-1"
          label="City"
          htmlAttributes={{
            name: 'city',
            required: true,
            autoComplete: 'address-level2',
            'data-testid': `${testIdPrefix}input-address2`,
          }}
        />
        <TextInput
          value={data.postalCode || ''}
          onChange={handleInputChange}
          isDisabled={disabled}
          isFluid
          className="flex-1"
          label={getPostalCodeText(selectedCountry?.value)}
          htmlAttributes={{
            name: 'postalCode',
            required: true,
            autoComplete: 'postal-code',
            'data-testid': `${testIdPrefix}input-postal-code`,
          }}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <Select
          size="medium"
          type="select"
          selectProps={{
            options: countryList,
            value: selectedCountry ?? null,
            onChange: handleCountryChange,
            isDisabled: disabled,
            name: 'country',
            placeholder: 'Country',
            required: true,
          }}
          label="Country"
          className="flex-1"
          htmlAttributes={{
            'data-testid': `${testIdPrefix}select-country`,
          }}
        />
        <TextInput
          value={data.state || ''}
          onChange={handleInputChange}
          isDisabled={disabled}
          isFluid
          isOptional
          className="flex-1"
          label="State"
          htmlAttributes={{
            name: 'state',
            autoComplete: 'address-level1',
            'data-testid': `${testIdPrefix}input-state`,
          }}
        />
      </div>
    </div>
  );
}
